/**
 * Centralized formatting functions for attachments.
 * These utilities ensure consistent display across all attachment views.
 */

import { dateFormatting } from './dateFormatting';

/**
 * Format file size in bytes to human-readable string.
 * @param bytes - File size in bytes (optional)
 * @returns Formatted string like "1.2 MB" or "—" for null/undefined
 *
 * @example
 * formatFileSize(null) // "—"
 * formatFileSize(0) // "0 B"
 * formatFileSize(512) // "512 B"
 * formatFileSize(1024) // "1.0 KB"
 * formatFileSize(1048576) // "1.0 MB"
 * formatFileSize(1099511627776) // "1.0 TB"
 */
export const formatFileSize = (bytes?: number): string => {
  // Handle null/undefined
  if (bytes === null || bytes === undefined) {
    return '—';
  }

  // Handle 0
  if (bytes === 0) {
    return '0 B';
  }

  // Handle negative (shouldn't happen, but be defensive)
  if (bytes < 0) {
    return '—';
  }

  // Less than 1KB - show bytes
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = bytes / 1024; // Start with KB
  let unitIndex = 0;

  // Keep dividing by 1024 until we find the right unit
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  // Format with 1 decimal place
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

/**
 * Format ISO date string to human-readable format.
 * @param dateString - ISO date string (optional)
 * @returns Formatted string like "Feb 10, 2026" or "—" for invalid dates
 *
 * @example
 * formatDate(null) // "—"
 * formatDate(undefined) // "—"
 * formatDate("") // "—"
 * formatDate("invalid") // "Invalid date"
 * formatDate("2026-02-10T10:00:00Z") // "Feb 10, 2026"
 */
export const formatDate = (dateString?: string): string => {
  if (!dateString) {
    return '—';
  }

  return (
    dateFormatting.format(dateString, {
      locale: 'en-US',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) ?? 'Invalid date'
  );
};

/**
 * Smart filename truncation that preserves file extension.
 * @param name - Filename to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated filename with preserved extension
 *
 * @example
 * formatFileName("", 30) // ""
 * formatFileName("a.pdf", 30) // "a.pdf"
 * formatFileName("VeryLongDocumentName123456.pdf", 30) // "VeryLongDocume...3456.pdf"
 * formatFileName("NoExtension", 10) // "NoExtensio..."
 */
export const formatFileName = (name: string, maxLength: number): string => {
  // Handle empty string
  if (!name) {
    return '';
  }

  // If name fits, return as-is
  if (name.length <= maxLength) {
    return name;
  }

  // Find the last dot for extension
  const lastDotIndex = name.lastIndexOf('.');

  // If no extension or extension is at the start, just truncate with ellipsis
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return name.substring(0, maxLength - 3) + '...';
  }

  // Extract extension
  const ext = name.substring(lastDotIndex);

  // Calculate how much space we have for the basename
  const baseMaxLength = maxLength - ext.length - 3; // -3 for "..."

  // If we don't have enough space for a reasonable basename, use simple truncation
  if (baseMaxLength < 10) {
    return name.substring(0, 10) + '...' + name.substring(name.length - 7);
  }

  // Extract basename and truncate
  const basename = name.substring(0, lastDotIndex);
  const truncatedBasename = basename.substring(0, baseMaxLength);

  return truncatedBasename + '...' + ext;
};
